import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { referralPartnerApi } from '@/api/referralPartner';
import PartnerLeadsTable from '@/components/partners/PartnerLeadsTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ALL_PARTNERS = '__all__';

type HospitalPartnerRow = {
  partner: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
  };
};

export default function PartnerLeadsPage() {
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>(ALL_PARTNERS);

  const { data: partners = [], isLoading: partnersLoading } = useQuery({
    queryKey: ['hospital-partners'],
    queryFn: async () => {
      const res = await referralPartnerApi.admin.hospital.partners();
      return (res.data?.data ?? []) as HospitalPartnerRow[];
    },
  });

  const partnerIdParam = selectedPartnerId === ALL_PARTNERS ? undefined : selectedPartnerId;

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['hospital-partner-leads', partnerIdParam ?? 'all'],
    queryFn: async () => {
      const res = await referralPartnerApi.admin.hospital.partnerLeads(partnerIdParam);
      return res.data?.data ?? [];
    },
  });

  const partnerOptions = useMemo(() => partners.map((p) => p.partner), [partners]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">Partner leads</h1>
        <p className="text-gray-500">Referral leads attributed to partners for your hospital scope.</p>
      </div>

      <div className="w-full sm:w-96">
        <label className="text-xs text-gray-500">Filter by partner</label>
        <Select value={selectedPartnerId} onValueChange={setSelectedPartnerId}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={partnersLoading ? 'Loading…' : 'All partners'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_PARTNERS}>All partners</SelectItem>
            {partnerOptions.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.firstName} {p.lastName} ({p.phone})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {leadsLoading ? (
        <p className="text-sm text-artis-slate">Loading leads…</p>
      ) : (
        <PartnerLeadsTable
          leads={leads as any[]}
          showPartnerColumn={selectedPartnerId === ALL_PARTNERS}
          emptyMessage={
            !partnersLoading && partners.length === 0
              ? 'No partner referrals for this hospital yet.'
              : 'No leads found for this filter.'
          }
        />
      )}
    </div>
  );
}
